import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, DollarSign, Copy, ExternalLink, Trash2, X, Check, TrendingUp, Users, CircleDollarSign, Mail } from "lucide-react";

/**
 * Admin-only TikTok Creator Incentive Program dashboard.
 * Lists creators with sign-ups / conversions / earnings + lets admin add/edit/pay/delete them.
 */
export default function AdminCreatorsTab() {
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", promo_code: "", payout_per_conversion: 5 });
  const [payoutTarget, setPayoutTarget] = useState(null); // creator obj
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutNote, setPayoutNote] = useState("");
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/creators");
      setList(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setList([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.promo_code) { toast.error("Fill in name, email, and promo code"); return; }
    setCreating(true);
    try {
      await api.post("/admin/creators", { ...form, promo_code: form.promo_code.toUpperCase() });
      toast.success(`${form.name} added — code ${form.promo_code.toUpperCase()} is live`);
      setForm({ name: "", email: "", promo_code: "", payout_per_conversion: 5 });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setCreating(false);
  };

  const del = async (c) => {
    if (!window.confirm(`Remove ${c.name}? Their attribution history stays, but they can't take new sign-ups.`)) return;
    try {
      await api.delete(`/admin/creators/${c.id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) { toast.error("Enter a payout amount"); return; }
    try {
      await api.post(`/admin/creators/${payoutTarget.id}/payout`, { amount: amt, note: payoutNote });
      toast.success(`Logged $${amt.toFixed(2)} payout to ${payoutTarget.name}`);
      setPayoutTarget(null); setPayoutAmount(""); setPayoutNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const sendTestEmail = async () => {
    const email = testEmail.trim();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSendingTest(true);
    try {
      await api.post("/admin/creators/test-welcome-email", { email });
      toast.success(`Test welcome email sent to ${email}`);
      setTestEmailOpen(false);
      setTestEmail("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSendingTest(false);
  };

  return (
    <div data-testid="admin-creators-tab">
      {/* Add creator form */}
      <div className="cp-card p-4 sm:p-5 mb-5">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#F4A83B]" />
            <h3 className="font-heading text-lg text-[#3B2C24]">Add a TikTok Creator</h3>
          </div>
          <button
            onClick={() => setTestEmailOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F7E5] border border-[#8EAC50]/30 text-[#5E7A2E] text-xs font-semibold hover:bg-[#e7f0d6] transition"
            title="Preview the welcome email in your own inbox"
            data-testid="preview-welcome-email-btn"
          >
            <Mail className="w-3.5 h-3.5" /> Preview welcome email
          </button>
        </div>
        <div className="grid sm:grid-cols-4 gap-2">
          <input placeholder="Name (e.g. Jane Baker)" className="cp-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="creator-form-name" />
          <input placeholder="Email" type="email" className="cp-input" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="creator-form-email" />
          <input placeholder="Promo code (e.g. BAKERJANE)" className="cp-input uppercase" value={form.promo_code} onChange={(e) => setForm((f) => ({ ...f, promo_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 40) }))} data-testid="creator-form-code" />
          <div className="flex gap-2">
            <input placeholder="$ per conv" type="number" step="0.5" className="cp-input flex-1" value={form.payout_per_conversion} onChange={(e) => setForm((f) => ({ ...f, payout_per_conversion: parseFloat(e.target.value) || 0 }))} data-testid="creator-form-payout" />
            <button onClick={create} disabled={creating} className="cp-btn-primary inline-flex items-center gap-1 disabled:opacity-60 shrink-0" data-testid="creator-form-add">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>
      </div>

      {/* Creator list */}
      {list === null ? (
        <div className="text-sm text-[#857163]">Loading…</div>
      ) : list.length === 0 ? (
        <div className="cp-card p-6 text-center text-sm text-[#857163]">No creators yet. Add one above.</div>
      ) : (
        <div className="space-y-3">
          {list.map((c) => (
            <div key={c.id} className="cp-card p-4 sm:p-5" data-testid={`creator-row-${c.promo_code}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="font-heading text-lg text-[#3B2C24]">{c.name}</div>
                  <div className="text-xs text-[#857163]">{c.email}</div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <button onClick={() => copy(c.promo_code)} title="Copy code" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#FFD07F]/40 text-[#3B2C24] font-bold text-xs" data-testid={`copy-code-${c.promo_code}`}>
                      {c.promo_code} <Copy className="w-3 h-3" />
                    </button>
                    <button onClick={() => copy(`${window.location.origin}/join/${c.referral_slug}`)} title="Copy referral link" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#EAF3FE] text-[#1D5FBF] text-xs" data-testid={`copy-link-${c.promo_code}`}>
                      /join/{c.referral_slug} <Copy className="w-3 h-3" />
                    </button>
                    {c.dashboard_token ? (
                      <>
                        <button onClick={() => copy(`${window.location.origin}/creator-dashboard/${c.dashboard_token}`)} title="Copy private dashboard link" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#F1F7E5] text-[#5E7A2E] text-xs" data-testid={`copy-dashboard-${c.promo_code}`}>
                          copy dashboard link <Copy className="w-3 h-3" />
                        </button>
                        <a href={`/creator-dashboard/${c.dashboard_token}`} target="_blank" rel="noopener noreferrer" title="Open their private dashboard" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#F1F7E5] text-[#5E7A2E] text-xs" data-testid={`open-dashboard-${c.promo_code}`}>
                          open <ExternalLink className="w-3 h-3" />
                        </a>
                      </>
                    ) : (
                      <span className="text-xs text-[#C2185B]">no dashboard token</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <button onClick={() => setPayoutTarget(c)} className="cp-btn-primary text-sm inline-flex items-center gap-1" data-testid={`pay-${c.promo_code}`}>
                    <DollarSign className="w-4 h-4" /> Pay
                  </button>
                  <button onClick={() => del(c)} className="p-2 text-[#857163] hover:text-[#C2185B]" title="Remove" data-testid={`del-${c.promo_code}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4 text-sm">
                <Stat icon={<Users className="w-4 h-4 text-[#8EAC50]" />} label="Sign-ups" value={c.signups} />
                <Stat icon={<TrendingUp className="w-4 h-4 text-[#2F80ED]" />} label="Paying" value={c.conversions} />
                <Stat icon={<CircleDollarSign className="w-4 h-4 text-[#F4A83B]" />} label="Earned" value={`$${c.earned.toFixed(2)}`} />
                <Stat icon={<Check className="w-4 h-4 text-[#5E7A2E]" />} label="Paid" value={`$${c.paid.toFixed(2)}`} />
                <Stat icon={<DollarSign className="w-4 h-4 text-[#C2185B]" />} label={c.owed < 0 ? "Credit" : "Owed"} value={`$${Math.abs(c.owed).toFixed(2)}`} highlight={c.owed > 0} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Payout modal */}
      {payoutTarget && (
        <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={() => setPayoutTarget(null)}>
          <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading text-lg text-[#3B2C24]">Log payout to {payoutTarget.name}</div>
              <button onClick={() => setPayoutTarget(null)} className="text-[#857163] hover:text-[#3B2C24]"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-[#857163] mb-3">Currently owed: <b>${payoutTarget.owed.toFixed(2)}</b></div>
            <label className="block mb-3">
              <span className="text-xs text-[#857163]">Amount ($)</span>
              <input type="number" step="0.01" className="cp-input mt-1" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} placeholder={payoutTarget.owed.toFixed(2)} autoFocus data-testid="payout-amount" />
            </label>
            <label className="block mb-4">
              <span className="text-xs text-[#857163]">Note (optional)</span>
              <input className="cp-input mt-1" value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)} placeholder="Venmo · @janebaker · Sept" data-testid="payout-note" />
            </label>
            <button onClick={submitPayout} className="cp-btn-primary w-full" data-testid="payout-submit">Log payment</button>
          </div>
        </div>
      )}

      {/* Test welcome email modal */}
      {testEmailOpen && (
        <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={() => setTestEmailOpen(false)}>
          <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="preview-welcome-email-modal">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 font-heading text-lg text-[#3B2C24]">
                <Mail className="w-4 h-4 text-[#5E7A2E]" /> Preview welcome email
              </div>
              <button onClick={() => setTestEmailOpen(false)} className="text-[#857163] hover:text-[#3B2C24]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#857163] mb-3">
              Sends a sample welcome email (with a fake promo code, referral link and dashboard link) to any address so you can see what real creators will receive.
            </p>
            <label className="block mb-4">
              <span className="text-xs text-[#857163]">Send test to</span>
              <input
                type="email"
                className="cp-input mt-1"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus
                data-testid="preview-welcome-email-input"
                onKeyDown={(e) => { if (e.key === "Enter") sendTestEmail(); }}
              />
            </label>
            <button
              onClick={sendTestEmail}
              disabled={sendingTest}
              className="cp-btn-primary w-full disabled:opacity-60"
              data-testid="preview-welcome-email-submit"
            >
              {sendingTest ? "Sending…" : "Send test email"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-2.5 border ${highlight ? "bg-[#C2185B]/10 border-[#C2185B]/30" : "bg-[#F9EFE6]/50 border-[#EBDCD1]"}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#857163]">{icon}{label}</div>
      <div className="text-lg font-bold text-[#3B2C24] mt-0.5">{value}</div>
    </div>
  );
}
